/**
 * Engine intents, their business labels and the Norican I2Q scope, in one place.
 *
 * The engine classifies every mail into one of seven intents (`rfq_quote`,
 * `order_place`, ...). Sales never see those keys: the queue's Type column and filter,
 * the case header and the add-in all print the label from here.
 *
 * Scope (decision of 9 Oct 2026): Norican I2Q has one purpose, customer inquiries to
 * quotations. Only `rfq_quote` is worked on. Every other intent is still classified so
 * the counts are known, but gets no customer or line matching, no prices, no reply
 * draft and no quotation preview. Purchase orders (`order_place`) are parked: the
 * server files them in the state `parked` and they show only under the queue tab
 * "Purchase orders (parked)". Every other kind of mail is under "Not an inquiry".
 * This mirrors the backend constant `rfq_foundry.scope.IN_SCOPE_INTENTS`.
 */

/** The seven engine intents, inquiries first. */
export const ENGINE_INTENTS = [
  "rfq_quote",
  "order_place",
  "order_status",
  "delivery_change",
  "invoice_finance",
  "complaint_quality",
  "general_other",
] as const;

export type EngineIntent = (typeof ENGINE_INTENTS)[number];

/** The intents Norican I2Q works on: customer inquiries only. */
export const IN_SCOPE_INTENTS: readonly EngineIntent[] = ["rfq_quote"];

/** Intents the engine used to work on and that are now parked (purchase orders). */
export const PARKED_INTENTS: readonly EngineIntent[] = ["order_place"];

const INTENT_LABELS: Record<EngineIntent, string> = {
  rfq_quote: "Inquiry",
  order_place: "Purchase order",
  order_status: "Order status",
  delivery_change: "Delivery change",
  invoice_finance: "Invoice / finance",
  complaint_quality: "Complaint",
  general_other: "Other",
};

/** Fixture / legacy UI values that still reach the mapper in the local dev server. */
const LEGACY_INTENTS: Record<string, EngineIntent> = {
  rfq: "rfq_quote",
  "price-indication": "rfq_quote",
  order: "order_place",
  "follow-up": "order_status",
  other: "general_other",
};

/** The engine intent of a wire or legacy value, or null when it is empty. */
function engineIntentOf(raw: unknown): EngineIntent | null {
  if (typeof raw !== "string" || raw.trim() === "") {
    return null;
  }
  const key = raw.trim().toLowerCase();
  if (key in INTENT_LABELS) {
    return key as EngineIntent;
  }
  return LEGACY_INTENTS[key] ?? "general_other";
}

/** Business label of an engine intent, or null when the mail is not classified yet. */
export function intentLabel(raw: unknown): string | null {
  const intent = engineIntentOf(raw);
  return intent ? INTENT_LABELS[intent] : null;
}

/**
 * True when Norican I2Q works on mail of this intent. A mail that is not classified
 * yet (null) counts as in scope, so a waiting case is not hidden as out of scope.
 */
export function isInScopeIntent(raw: unknown): boolean {
  const intent = engineIntentOf(raw);
  return intent === null || IN_SCOPE_INTENTS.includes(intent);
}

/** True for a purchase order: the intent Norican I2Q used to work on and now parks. */
export function isParkedIntent(raw: unknown): boolean {
  const intent = engineIntentOf(raw);
  return intent !== null && PARKED_INTENTS.includes(intent);
}

/** States in which a case was not classified yet or is only a copy of another case. */
const UNSETTLED_STATES: readonly string[] = ["queued", "processing", "duplicate"];

/**
 * True when a case is out of the Norican I2Q scope: the server parked it, or its
 * intent is not an inquiry. A case the server has not re-filed yet (an older order
 * still in `draft-ready`) counts as out of scope too, so no order shows quotation work.
 */
export function isOutOfScopeCase(row: {
  state: string;
  engineIntent?: string | null;
  intent?: string | null;
}): boolean {
  if (row.state === "parked") {
    return true;
  }
  if (UNSETTLED_STATES.includes(row.state)) {
    return false;
  }
  return !isInScopeIntent(row.engineIntent ?? row.intent ?? null);
}

/** One option of the queue's Type filter. */
export interface IntentOption {
  value: EngineIntent;
  label: string;
}

const option = (intent: EngineIntent): IntentOption => ({
  value: intent,
  label: INTENT_LABELS[intent],
});

/** The inquiry options: what the working tabs of the queue may filter on. */
export const INTENT_OPTIONS: readonly IntentOption[] = IN_SCOPE_INTENTS.map(option);

/**
 * The options of the All tab and of "Not an inquiry": every intent except the parked
 * purchase orders. "Not an inquiry" also holds mails first classified as an inquiry
 * that the engine's second look read as something else, so "Inquiry" is offered there.
 */
export const ALL_INTENT_OPTIONS: readonly IntentOption[] = ENGINE_INTENTS.filter(
  (intent) => !PARKED_INTENTS.includes(intent),
).map(option);

/** The queue tab of mail that is neither an inquiry nor a purchase order. */
export const NOT_AN_INQUIRY = "Not an inquiry";

/**
 * Type of a mail classified as an inquiry that the engine's second read of the full
 * text turned down (state `not-an-rfq`): both readings, so Type and Status agree.
 */
export const INQUIRY_DOWNGRADED = "Inquiry, then not a quote";
/** The "why" of such a mail. */
export const INQUIRY_DOWNGRADED_WHY =
  "Full text read: no request to quote (for example a reply in a thread)";

/** True when an inquiry by its first reading was turned down by the second read. */
export function isDowngradedInquiry(state: string, intent: unknown): boolean {
  if (state !== "not-an-rfq") {
    return false;
  }
  const engine = engineIntentOf(intent);
  return engine !== null && IN_SCOPE_INTENTS.includes(engine);
}

/** The queue tab of the parked purchase orders (view `parked`). */
export const PARKED_TAB = "Purchase orders (parked)";

/**
 * The Type filter of a queue tab offers only what the tab can hold: every intent but the
 * parked orders under "Not an inquiry" (`not-rfq`) and All, nothing for the parked tab
 * (orders only) or for mail that is not classified yet, and inquiries only in the
 * working tabs.
 */
export function intentOptionsFor(view: string): readonly IntentOption[] {
  switch (view) {
    case "not-rfq":
    case "all":
      return ALL_INTENT_OPTIONS;
    case "parked":
    case "waiting":
      return [];
    default:
      return INTENT_OPTIONS;
  }
}
